import { ErrorPage, RouteErrorBoundary } from './ErrorBoundary';
import { LoadingSkeleton as Spinner } from '@/components/ui/LoadingSkeleton';
import { createBrowserRouter } from 'react-router';
import { MonthIndexRedirect } from '@/features/month/pages/MonthIndexRedirect';
import { HouseholdIndexRedirect } from './HouseholdIndexRedirect';

export const router = createBrowserRouter(
  [
    {
      path: '/',
      lazy: async () => ({
        Component: (await import('@/features/landing/LandingEntry')).LandingEntry,
      }),
    },
    {
      path: '/welcome',
      lazy: async () => ({
        Component: (await import('@/features/landing/LandingPage')).LandingPage,
      }),
    },
    {
      path: '/privacy',
      lazy: async () => ({
        Component: (await import('@/features/landing/PrivacyPage')).PrivacyPage,
      }),
    },
    {
      path: '/signin',
      lazy: async () => ({
        Component: (await import('@/features/auth/pages/SignInPage')).SignInPage,
      }),
    },
    {
      path: '/auth/callback',
      lazy: async () => ({
        Component: (await import('@/features/auth/pages/AuthCallbackPage')).AuthCallbackPage,
      }),
    },
    {
      path: '/onboarding',
      lazy: async () => ({
        Component: (await import('@/features/onboarding/pages/OnboardingPage')).OnboardingPage,
      }),
    },
    {
      path: '/h/:hid/onboarding/demo',
      lazy: async () => ({
        Component: (await import('@/features/onboarding/demo/PracticeTour')).PracticeTour,
      }),
    },
    {
      path: '/h/:hid',
      lazy: async () => ({ Component: (await import('./layouts/AppShell')).AppShell }),
      children: [
        { index: true, element: <HouseholdIndexRedirect /> },
        {
          path: 'onboarding',
          lazy: async () => ({
            Component: (await import('@/features/onboarding/pages/HouseholdOnboarding'))
              .HouseholdOnboarding,
          }),
        },
        { path: 'month', element: <MonthIndexRedirect /> },
        {
          path: 'month/:period',
          lazy: async () => ({
            Component: (await import('@/features/month/pages/MonthPage')).MonthPage,
          }),
        },
        {
          path: 'accounts',
          lazy: async () => ({
            Component: (await import('@/features/accounts/pages/AccountsPage')).AccountsPage,
          }),
        },
        {
          path: 'cards',
          lazy: async () => ({
            Component: (await import('@/features/cards/pages/CardsPage')).CardsPage,
          }),
        },
        {
          path: 'cards/:accountId/import/:importId',
          lazy: async () => ({
            Component: (await import('@/features/cards/pages/ImportWizardPage')).ImportWizardPage,
          }),
        },
        {
          path: 'cards/:accountId/import',
          lazy: async () => ({
            Component: (await import('@/features/cards/pages/ImportWizardPage')).ImportWizardPage,
          }),
        },
        {
          path: 'cards/:accountId/:period',
          lazy: async () => ({
            Component: (await import('@/features/cards/pages/StatementPage')).StatementPage,
          }),
        },
        {
          path: 'bills',
          lazy: async () => ({
            Component: (await import('@/features/recurring/pages/BillsPage')).BillsPage,
          }),
        },
        {
          path: 'insights',
          lazy: async () => ({
            Component: (await import('@/features/insights/pages/InsightsPage')).InsightsPage,
          }),
        },
        {
          path: 'investments',
          lazy: async () => ({
            Component: (await import('@/features/investments/InvestmentsPage')).InvestmentsPage,
          }),
        },
        {
          path: 'borrowings',
          lazy: async () => ({
            Component: (await import('@/features/borrowings/LoansBorrowingsPage'))
              .LoansBorrowingsPage,
          }),
        },
        {
          path: 'wealth',
          lazy: async () => ({
            Component: (await import('@/features/wealth/pages/WealthPage')).WealthPage,
          }),
        },
        {
          path: 'borrowings/loans/:loanId',
          lazy: async () => ({
            Component: (await import('@/features/wealth/pages/LoanDetailPage')).LoanDetailPage,
          }),
        },
        {
          path: 'wealth/loans/:loanId',
          lazy: async () => ({
            Component: (await import('@/features/wealth/pages/LoanDetailPage')).LoanDetailPage,
          }),
        },
        {
          path: 'template',
          lazy: async () => ({
            Component: (await import('@/features/template/pages/TemplatePage')).TemplatePage,
          }),
        },
        {
          path: 'inbox',
          lazy: async () => ({
            Component: (await import('@/features/notifications/pages/InboxPage')).InboxPage,
          }),
        },
        {
          path: 'documents',
          lazy: async () => ({
            Component: (await import('@/features/storage/DocumentsPage')).DocumentsPage,
          }),
        },
        {
          path: 'settings',
          lazy: async () => ({
            Component: (await import('@/features/settings/pages/SettingsPage')).SettingsPage,
          }),
        },
      ],
    },
    { path: '*', element: <ErrorPage notFound /> },
  ].map((route) => ({
    ...route,
    hydrateFallbackElement: <Spinner label="Loading FinBob" />,
    errorElement: <RouteErrorBoundary />,
  })),
);
